import { useState } from 'react';
import { CustomizedResume } from '../../types/resumeTuning';
import { ResumeData } from '../../services/resume';
import './ComparisonView.css';

interface ComparisonViewProps {
  baseResume: ResumeData;
  customizedResume: CustomizedResume;
  onStartOver: () => void;
}

type ViewMode = 'side-by-side' | 'customized' | 'base';

export function ComparisonView({
  baseResume,
  customizedResume,
  onStartOver,
}: ComparisonViewProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('customized');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      setExportError('');
      
      // Mock export - will use actual exportResumeToPDF when backend is ready
      const filename = `resume_${customizedResume.jobPosition.title.replace(/\s+/g, '_')}.pdf`;
      console.log('Exporting as:', filename);
    } catch (err: any) {
      setExportError(err.message || 'Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadJSON = () => {
    const dataStr = JSON.stringify(customizedResume, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `resume_${customizedResume.id}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="comparison-view">
      <div className="comparison-header">
        <h2>Customized Resume</h2>
        <p>
          Your resume tailored for: <strong>{customizedResume.jobPosition.title}</strong> at{' '}
          <strong>{customizedResume.jobPosition.company}</strong>
        </p>
      </div>

      <div className="view-controls">
        <div className="view-modes">
          <button
            className={`view-btn ${viewMode === 'customized' ? 'active' : ''}`}
            onClick={() => setViewMode('customized')}
          >
            Customized Version
          </button>
          <button
            className={`view-btn ${viewMode === 'side-by-side' ? 'active' : ''}`}
            onClick={() => setViewMode('side-by-side')}
          >
            Compare
          </button>
          <button
            className={`view-btn ${viewMode === 'base' ? 'active' : ''}`}
            onClick={() => setViewMode('base')}
          >
            Original Version
          </button>
        </div>
      </div>

      {exportError && (
        <div className="alert alert-error">
          <strong>Export Error:</strong> {exportError}
        </div>
      )}

      <div className="comparison-content">
        {viewMode === 'customized' && (
          <div className="resume-preview customized-preview">
            <div className="resume-section personal-info">
              <h3>{baseResume.personalInfo.name}</h3>
              <p>{baseResume.personalInfo.email}</p>
            </div>

            <div className="resume-section">
              <h4>Professional Summary</h4>
              <p>{customizedResume.customization.summary}</p>
            </div>

            {customizedResume.customization.experience.length > 0 && (
              <div className="resume-section">
                <h4>Experience</h4>
                {customizedResume.customization.experience.map((exp: any, idx: number) => (
                  <div key={idx} className="experience-item">
                    <h5>{exp.original?.position || exp.position || 'Position'}</h5>
                    <p className="company-info">{exp.original?.company || exp.company || ''}</p>
                    <ul className="achievements">
                      {(exp.tailoredVersion?.achievements || exp.original?.achievements || []).map(
                        (achievement: string, aidx: number) => (
                          <li key={aidx}>{achievement}</li>
                        )
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {customizedResume.customization.skills && (
              <div className="resume-section">
                <h4>Skills</h4>
                {customizedResume.customization.skills.technical.length > 0 && (
                  <div className="skills-category">
                    <h5>Technical</h5>
                    <div className="skills-list">
                      {customizedResume.customization.skills.technical.map((skill: any, idx: number) => (
                        <span key={idx} className={`skill-tag ${skill.highlighted ? 'highlighted' : ''}`}>
                          {skill.skill}
                          {skill.highlighted && <span className="highlight-marker">★</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {customizedResume.customization.skills.soft.length > 0 && (
                  <div className="skills-category">
                    <h5>Soft Skills</h5>
                    <div className="skills-list">
                      {customizedResume.customization.skills.soft.map((skill: any, idx: number) => (
                        <span key={idx} className={`skill-tag ${skill.highlighted ? 'highlighted' : ''}`}>
                          {skill.skill}
                          {skill.highlighted && <span className="highlight-marker">★</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {viewMode === 'side-by-side' && (
          <div className="comparison-container">
            <div className="resume-column">
              <h3>Original Resume</h3>
              <div className="resume-preview">
                <div className="resume-section personal-info">
                  <h4>{baseResume.personalInfo.name}</h4>
                  <p>{baseResume.personalInfo.email}</p>
                </div>

                {baseResume.experience.length > 0 && (
                  <div className="resume-section">
                    <h5>Experience</h5>
                    {baseResume.experience.map((exp: any, idx: number) => (
                      <div key={idx} className="experience-item">
                        <h6>{exp.position || exp.title || 'Position'}</h6>
                        <p className="company-info">{exp.company}</p>
                      </div>
                    ))}
                  </div>
                )}

                {baseResume.skills.technical.length > 0 && (
                  <div className="resume-section">
                    <h5>Technical Skills</h5>
                    <div className="skills-list">
                      {baseResume.skills.technical.map((skill, idx) => (
                        <span key={idx} className="skill-tag">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="resume-column">
              <h3>Customized for This Job</h3>
              <div className="resume-preview">
                <div className="resume-section personal-info">
                  <h4>{baseResume.personalInfo.name}</h4>
                  <p>{baseResume.personalInfo.email}</p>
                </div>

                <div className="resume-section">
                  <h5>Professional Summary</h5>
                  <p>{customizedResume.customization.summary}</p>
                </div>

                {customizedResume.customization.experience.length > 0 && (
                  <div className="resume-section">
                    <h5>Experience</h5>
                    {customizedResume.customization.experience.map((exp: any, idx: number) => (
                      <div key={idx} className="experience-item">
                        <h6>{exp.original?.position || exp.position || 'Position'}</h6>
                        <p className="company-info">{exp.original?.company || exp.company || ''}</p>
                        <ul className="achievements">
                          {(exp.tailoredVersion?.achievements || exp.original?.achievements || [])
                            .slice(0, 3)
                            .map((achievement: string, aidx: number) => (
                              <li key={aidx}>{achievement}</li>
                            ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {customizedResume.customization.skills && (
                  <div className="resume-section">
                    <h5>Highlighted Skills</h5>
                    <div className="skills-list">
                      {[
                        ...customizedResume.customization.skills.technical,
                        ...customizedResume.customization.skills.soft,
                      ]
                        .filter((s: any) => s.highlighted)
                        .map((skill: any, idx: number) => (
                          <span key={idx} className="skill-tag highlighted">
                            {skill.skill}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {viewMode === 'base' && (
          <div className="resume-preview base-preview">
            <div className="resume-section personal-info">
              <h3>{baseResume.personalInfo.name}</h3>
              <p>{baseResume.personalInfo.email}</p>
            </div>

            {baseResume.experience.length > 0 && (
              <div className="resume-section">
                <h4>Experience</h4>
                {baseResume.experience.map((exp: any, idx: number) => (
                  <div key={idx} className="experience-item">
                    <h5>{exp.position || exp.title || 'Position'}</h5>
                    <p className="company-info">{exp.company}</p>
                    <p className="duration">
                      {exp.startDate} - {exp.endDate || 'Present'}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {baseResume.education.length > 0 && (
              <div className="resume-section">
                <h4>Education</h4>
                {baseResume.education.map((edu: any, idx: number) => (
                  <div key={idx} className="education-item">
                    <h5>{edu.degree}</h5>
                    <p className="school">{edu.institution || edu.school}</p>
                  </div>
                ))}
              </div>
            )}

            {(baseResume.skills.technical.length > 0 || baseResume.skills.soft.length > 0) && (
              <div className="resume-section">
                <h4>Skills</h4>
                {baseResume.skills.technical.length > 0 && (
                  <div className="skills-category">
                    <h5>Technical</h5>
                    <div className="skills-list">
                      {baseResume.skills.technical.map((skill, idx) => (
                        <span key={idx} className="skill-tag">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {baseResume.skills.soft.length > 0 && (
                  <div className="skills-category">
                    <h5>Soft Skills</h5>
                    <div className="skills-list">
                      {baseResume.skills.soft.map((skill, idx) => (
                        <span key={idx} className="skill-tag">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="comparison-actions">
        <div className="action-buttons">
          <button
            className="btn btn-primary"
            onClick={handleExportPDF}
            disabled={exporting}
          >
            {exporting ? '📥 Exporting...' : '📥 Export as PDF'}
          </button>
          <button className="btn btn-secondary" onClick={handleDownloadJSON}>
            💾 Save as JSON
          </button>
          <button className="btn btn-outline" onClick={onStartOver}>
            🔄 Create Another
          </button>
        </div>
      </div>

      <div className="next-steps">
        <h3>Next Steps</h3>
        <ul>
          <li>✓ Review the customized resume above</li>
          <li>📥 Export as PDF to use in your application</li>
          <li>💾 Save the JSON version to revisit later</li>
          <li>📝 Customize further if needed, or create another for a different job</li>
        </ul>
      </div>
    </div>
  );
}
